import { create } from 'zustand';
import type { N8nCredentialSummary, N8nCredentialSchema } from '@n8n-builder/api-client';

export interface CredentialRequirement {
  credentialType: string;
  nodeNames: string[];
  isOAuth2: boolean;
  mappedCredential?: { id: string; name: string };
}

type PanelState = 'idle' | 'loading' | 'ready' | 'error';

interface CredentialsState {
  panelState: PanelState;
  errorMessage: string | null;
  availableCredentials: N8nCredentialSummary[];
  requirements: CredentialRequirement[];
  schemas: Record<string, N8nCredentialSchema>;
  activeFormType: string | null;
  applied: boolean;

  setPanelState: (state: PanelState, error?: string | null) => void;
  setAvailableCredentials: (credentials: N8nCredentialSummary[]) => void;
  setRequirements: (requirements: CredentialRequirement[]) => void;
  addSchema: (type: string, schema: N8nCredentialSchema) => void;
  mapCredential: (credentialType: string, credential: { id: string; name: string }) => void;
  unmapCredential: (credentialType: string) => void;
  setActiveFormType: (type: string | null) => void;
  addAvailableCredential: (credential: N8nCredentialSummary) => void;
  setApplied: (applied: boolean) => void;
  reset: () => void;
}

export const useCredentialsStore = create<CredentialsState>((set) => ({
  panelState: 'idle',
  errorMessage: null,
  availableCredentials: [],
  requirements: [],
  schemas: {},
  activeFormType: null,
  applied: false,

  setPanelState: (panelState, errorMessage = null) =>
    set({ panelState, errorMessage }),

  setAvailableCredentials: (availableCredentials) =>
    set({ availableCredentials }),

  setRequirements: (requirements) =>
    set({ requirements }),

  addSchema: (type, schema) =>
    set((state) => ({
      schemas: { ...state.schemas, [type]: schema },
    })),

  mapCredential: (credentialType, credential) =>
    set((state) => ({
      applied: false,
      requirements: state.requirements.map((req) =>
        req.credentialType === credentialType
          ? { ...req, mappedCredential: credential }
          : req
      ),
    })),

  unmapCredential: (credentialType) =>
    set((state) => ({
      applied: false,
      requirements: state.requirements.map((req) =>
        req.credentialType === credentialType
          ? { ...req, mappedCredential: undefined }
          : req
      ),
    })),

  setActiveFormType: (activeFormType) =>
    set({ activeFormType }),

  addAvailableCredential: (credential) =>
    set((state) => ({
      availableCredentials: [...state.availableCredentials, credential],
    })),

  setApplied: (applied) =>
    set({ applied }),

  reset: () =>
    set({
      panelState: 'idle',
      errorMessage: null,
      availableCredentials: [],
      requirements: [],
      schemas: {},
      activeFormType: null,
      applied: false,
    }),
}));
